/**
 * Web app onboarding application shell
 *
 * Bootstraps session-scoped Firebase Auth, consumes invitation `?s=` into the store,
 * wires PostHog and Chatwoot, listens to `signedUpPatients`, and mounts the route table.
 *
 * References:
 * - `routing/OnboardingRouter.tsx`
 * - `docs/PF-4846/implementation-overview.md`
 */

import { PostHogErrorBoundary, PostHogProvider } from '@posthog/react';
import { onAuthStateChanged } from 'firebase/auth';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ConfirmationModal } from '../../components/confirmationModal/ConfirmationModal';
import { EventIndicator } from '../../components/eventIndicator/EventIndicator';
import { auth, ensureWebAppOnboardingAuthSessionPersistence } from '../../firebaseConfig';
import { OnboardingFlowLoadingScreen } from './components/layout/OnboardingFlowLoadingScreen';
import { useSignedUpPatientConnect } from './hooks/useSignedUpPatientConnect';
import { getChatwoot } from './integrations/chatwoot/chatwootConfig';
import { identifyOnboardingChatwootUser } from './integrations/chatwoot/identifyOnboardingChatwootUser';
import { OnboardingChatwootOpeningOverlay } from './integrations/chatwoot/OnboardingChatwootOpeningOverlay';
import { OnboardingChatwootProvider } from './integrations/chatwoot/OnboardingChatwootProvider';
import { identifyBySignUpId, initOnboardingAnalytics } from './integrations/posthog/init';
import { OnboardingRouter } from './routing/OnboardingRouter';
import { useTrackOnboardingStepShown } from './routing/useTrackOnboardingStepShown';
import { getSignUpId, useOnboardingStore } from './store/onboardingStore';
import { resolvePatientDisplayName } from './utils/resolvePatientDisplayName';

const SIGN_UP_ID_QUERY_KEY = 's';
const UTM_SOURCE_QUERY_KEY = 'u';

const OnboardingShownTracker = () => {
  useTrackOnboardingStepShown();
  return null;
};

/**
 * Root React tree for `/webAppOnboarding/*` (providers, router, global overlays).
 *
 * @return {Object} PostHog-wrapped onboarding layout.
 */
export const WebAppOnboardingApp = () => {
  const [authPersistenceReady, setAuthPersistenceReady] = useState(false);
  useSignedUpPatientConnect();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setSignUpId = useOnboardingStore((state) => state.setSignUpId);
  const setPendingLinkUtmCode = useOnboardingStore((state) => state.setPendingLinkUtmCode);
  const resetInviteContextCache = useOnboardingStore((state) => state.resetInviteContextCache);

  useEffect(() => {
    void ensureWebAppOnboardingAuthSessionPersistence().then(
      () => setAuthPersistenceReady(true),
      (err: unknown) => {
        console.error('[webAppOnboarding] ensureWebAppOnboardingAuthSessionPersistence failed', err);
        setAuthPersistenceReady(true);
      },
    );
  }, []);

  useEffect(() => {
    initOnboardingAnalytics();
  }, []);

  const signedUpPatientId = useOnboardingStore(getSignUpId);

  useEffect(() => {
    const fromQuery = searchParams.get(SIGN_UP_ID_QUERY_KEY)?.trim();
    const utmSource = searchParams.get(UTM_SOURCE_QUERY_KEY)?.trim();

    if (!fromQuery) {
      return;
    }
    resetInviteContextCache();
    setPendingLinkUtmCode(utmSource);
    setSignUpId(fromQuery);
    navigate({ pathname: location.pathname, hash: location.hash }, { replace: true });
  }, [
    searchParams,
    resetInviteContextCache,
    setPendingLinkUtmCode,
    setSignUpId,
    navigate,
    location.pathname,
    location.hash,
  ]);

  useEffect(() => {
    if (signedUpPatientId) {
      identifyBySignUpId(signedUpPatientId);
    } else {
      posthog.reset();
    }
  }, [signedUpPatientId]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user || !getChatwoot()) return;
      const { signedUpPatient, verifiedPatientNamesFromAuth } = useOnboardingStore.getState();
      if (!signedUpPatient) return;
      await identifyOnboardingChatwootUser(
        signedUpPatient,
        resolvePatientDisplayName(signedUpPatient, verifiedPatientNamesFromAuth),
      );
    });
    return unsub;
  }, []);

  if (!authPersistenceReady) {
    return <OnboardingFlowLoadingScreen />;
  }

  return (
    <PostHogProvider client={posthog}>
      <PostHogErrorBoundary>
        <OnboardingShownTracker />
        <OnboardingChatwootProvider>
          <OnboardingRouter />
          <EventIndicator />
          <ConfirmationModal />
          <OnboardingChatwootOpeningOverlay />
        </OnboardingChatwootProvider>
      </PostHogErrorBoundary>
    </PostHogProvider>
  );
};
